import React, { useRef, useEffect, useState } from 'react';
import { AIMessage } from './AIMessage';
import { ThinkingIndicator } from './ThinkingIndicator';
import { StreamingMessage } from './StreamingMessage';
import { WelcomeScreen } from './WelcomeScreen';
import { SalesWelcomeScreen } from '../sales-chat/SalesWelcomeScreen';
import type { AIMessageListProps, AIQuestionAnswers, AIMessage as AIMessageType } from './types';

interface ExtendedMessageListProps extends AIMessageListProps {
  onSuggestionClick?: (text: string) => void;
  businessLogoUrl?: string | null;
  businessId?: string | null;
  onQuestionsSubmit?: (answers: AIQuestionAnswers, displayText: string) => void;
  streamingContent?: string;
  onResend?: (message: AIMessageType) => void;
  onEdit?: (message: AIMessageType, newContent: string) => void;
  conversationId?: string | null;
  autoAcceptEnabled?: boolean;
  mode?: 'assistant' | 'sales';
}

export const AIMessageList: React.FC<ExtendedMessageListProps> = ({
  messages,
  isLoading,
  onRevert,
  onConfirmTools,
  onCancelTools,
  onSuggestionClick,
  businessLogoUrl,
  businessId,
  onQuestionsSubmit,
  streamingContent,
  onResend,
  onEdit,
  conversationId,
  autoAcceptEnabled,
  mode = 'assistant',
}) => {
  const isSalesMode = mode === 'sales';
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Track message IDs that existed on first render - these should NEVER animate
  const initialMessageIdsRef = useRef<Set<string> | null>(null);

  // Track which new messages have already been animated
  const animatedNewMessagesRef = useRef<Set<string>>(new Set());

  // Track which conversation the refs were set for
  const conversationIdRef = useRef<string | null>(null);

  // The single message ID currently being animated (if any)
  const [animatingId, setAnimatingId] = useState<string | null>(null);

  // Track which messages have been auto-confirmed to prevent double-confirmation
  const autoConfirmedRef = useRef<Set<string>>(new Set());

  // Reset refs when conversation changes
  if (conversationId !== conversationIdRef.current) {
    conversationIdRef.current = conversationId ?? null;
    initialMessageIdsRef.current = null;
    animatedNewMessagesRef.current = new Set();
    autoConfirmedRef.current = new Set();
    setAnimatingId(null);
  }

  // On first render with messages, capture all existing IDs
  // These are "old" messages loaded from DB and should never animate
  if (initialMessageIdsRef.current === null && messages.length > 0) {
    initialMessageIdsRef.current = new Set(messages.map(m => m.id));
  }

  // Auto-scroll to bottom when new messages arrive or streaming content changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, streamingContent]);

  // Detect truly new assistant messages and trigger animation
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    // Only animate if:
    // 1. It's an assistant message
    // 2. It wasn't in the initial load (not in initialMessageIdsRef)
    // 3. It hasn't been animated yet (not in animatedNewMessagesRef)
    const isAssistant = lastMessage.role === 'assistant';
    const wasInitial = initialMessageIdsRef.current?.has(lastMessage.id) ?? false;
    const alreadyAnimated = animatedNewMessagesRef.current.has(lastMessage.id);

    if (isAssistant && !wasInitial && !alreadyAnimated) {
      // This is a brand new message - animate it
      setAnimatingId(lastMessage.id);
      animatedNewMessagesRef.current.add(lastMessage.id);
    }
  }, [messages]);

  // Auto-confirm pending tools when auto-accept mode is enabled
  useEffect(() => {
    if (!autoAcceptEnabled || !onConfirmTools || messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    // Check if this message has pending tools that haven't been auto-confirmed yet
    if (
      lastMessage.role === 'assistant' &&
      lastMessage.pendingTools &&
      lastMessage.pendingTools.length > 0 &&
      !autoConfirmedRef.current.has(lastMessage.id)
    ) {
      // Mark as auto-confirmed before calling to prevent race conditions
      autoConfirmedRef.current.add(lastMessage.id);

      // Small delay to ensure UI has rendered the pending state
      const timeoutId = setTimeout(() => {
        onConfirmTools(lastMessage.pendingTools!);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [messages, autoAcceptEnabled, onConfirmTools]);

  if (messages.length === 0 && !isLoading) {
    return isSalesMode ? (
      <SalesWelcomeScreen onSuggestionClick={onSuggestionClick || (() => {})} />
    ) : (
      <WelcomeScreen onSuggestionClick={onSuggestionClick || (() => {})} />
    );
  }

  return (
    <div className="flex flex-col">
      {messages.map((message) => {
        // Only pass isLatest=true for the message currently being animated
        const shouldAnimate = message.id === animatingId;

        return (
          <AIMessage
            key={message.id}
            message={message}
            onRevert={onRevert}
            onConfirmTools={onConfirmTools}
            onCancelTools={onCancelTools}
            isLatest={shouldAnimate}
            businessLogoUrl={businessLogoUrl}
            businessId={businessId}
            onQuestionsSubmit={onQuestionsSubmit}
            onResend={onResend}
            onEdit={onEdit}
          />
        );
      })}

      {/* Streaming content or thinking indicator */}
      {isLoading && (
        streamingContent ? (
          <StreamingMessage content={streamingContent} />
        ) : (
          <ThinkingIndicator />
        )
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default AIMessageList;
